/**
 * Typed fetch wrapper for the FastAPI backend.
 * - base path '/api' (Vite proxies to :8000 in dev; same-origin in prod)
 * - sends cookies (credentials: 'include')
 * - JSON request/response
 * - throws ApiError on non-2xx (with the backend's machine-readable `code`)
 * - throws NetworkError when the request never got a response
 * - on 401, redirects to /login (the cookie/session expired or is missing)
 *
 * Also exports buildWindowQuery — the canonical query-string builder for the
 * shared `?days=N&end=YYYY-MM-DD` window pattern used by tracker and analytics
 * endpoints.
 */

export const API_BASE = "/api";

/**
 * Reliable fire-and-forget POST for leave-triggered saves (app backgrounded /
 * closed). Uses navigator.sendBeacon: the browser queues the request and
 * guarantees delivery even after the tab is frozen or unloaded — unlike a
 * keepalive fetch, which mobile browsers drop when a tab is merely hidden.
 * Same-origin, so the session cookie is sent automatically. Returns false if
 * the beacon could not be queued (size cap / unsupported).
 */
export function beacon(path: string, body: unknown): boolean {
  if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") return false;
  const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
  return navigator.sendBeacon(`${API_BASE}${path}`, blob);
}

/**
 * Builds the `?days=N&end=YYYY-MM-DD` query string for windowed API endpoints.
 * Returns an empty string when neither argument is provided.
 */
export function buildWindowQuery(days?: number, end?: string): string {
  const params = new URLSearchParams();
  if (days !== undefined) params.set("days", String(days));
  if (end !== undefined) params.set("end", end);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  /** The backend's machine-readable error code (e.g. "bq_unavailable"), when it sent one. */
  readonly code: string | null;
  /** The backend's own verdict on whether repeating the request can succeed. */
  readonly retryable: boolean;
  /** Retry-After, in ms, when the response carried one in seconds. */
  readonly retryAfterMs: number | null;

  constructor(status: number, message: string, body: unknown, retryAfterMs: number | null = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    const obj = body && typeof body === "object" ? (body as { code?: unknown; retryable?: unknown }) : null;
    this.code = typeof obj?.code === "string" ? obj.code : null;
    this.retryable = obj?.retryable === true;
    this.retryAfterMs = retryAfterMs;
  }
}

/**
 * The request never got a response: offline, DNS, a dropped connection. fetch
 * reports all of these as a bare TypeError, which is indistinguishable from a
 * programming error — so it is renamed at the one place it can be told apart.
 */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("Network request failed", { cause });
    this.name = "NetworkError";
  }
}

/**
 * Why a write failed, in the terms the UI reports it.
 *
 * - `bigquery` — the backend said BigQuery stalled or refused (503 bq_unavailable).
 *   The incident this exists for (2026-10-01): DML jobs sat until job_timeout_ms
 *   cancelled them while reads stayed fast, and the app could only say "failed".
 * - `server`   — a gateway-level 502/503/504 with no code: Cloud Run, not BigQuery.
 * - `network`  — no response at all.
 * - `other`    — everything else: validation, conflict, a plain 500. Repeating
 *   the same request cannot fix these.
 */
export type FailureCause = "bigquery" | "server" | "network" | "other";

export function failureCause(err: unknown): FailureCause {
  if (err instanceof NetworkError) return "network";
  if (err instanceof ApiError) {
    if (err.code === "bq_unavailable") return "bigquery";
    if (err.status === 502 || err.status === 503 || err.status === 504) return "server";
  }
  return "other";
}

/**
 * Whether repeating the identical request can succeed. Only ever applied to
 * IDEMPOTENT writes (the workout save and the measurement PUT, both
 * replace-by-date) — a retried non-idempotent write (rewards claim) could land
 * twice.
 */
export function isRetryable(err: unknown): boolean {
  return failureCause(err) !== "other";
}

/** Retries after the first attempt, for an idempotent save. */
export const SAVE_MAX_RETRIES = 3;
const SAVE_RETRY_SCHEDULE_MS = [3_000, 10_000, 30_000];

/**
 * TanStack Query `retry` for idempotent saves. `failureCount` is the number of
 * attempts that have already failed minus one (0 on the first failure).
 */
export function shouldRetrySave(failureCount: number, err: unknown): boolean {
  return failureCount < SAVE_MAX_RETRIES && isRetryable(err);
}

/**
 * TanStack Query `retryDelay` for idempotent saves: a fixed ~3s/10s/30s
 * backoff, never sooner than the server's Retry-After. Each attempt can itself
 * spend ~60s server-side before BigQuery's job timeout gives up, so hammering
 * faster than this only adds load to a backend that is already stalled.
 */
export function saveRetryDelay(failureCount: number, err: unknown): number {
  const scheduled = SAVE_RETRY_SCHEDULE_MS[Math.min(failureCount, SAVE_RETRY_SCHEDULE_MS.length - 1)]!;
  const floor = err instanceof ApiError && err.retryAfterMs != null ? Math.min(err.retryAfterMs, 60_000) : 0;
  return Math.max(scheduled, floor);
}

/** Retry-After in seconds → ms. The HTTP-date form is not used by this backend. */
function parseRetryAfter(value: string | null): number | null {
  if (value == null) return null;
  const secs = Number(value);
  return Number.isFinite(secs) && secs >= 0 ? secs * 1000 : null;
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  // keepalive is inherited from RequestInit via the Omit spread above;
  // re-declared here for explicit visibility at call sites.
  keepalive?: boolean;
}

function redirectToLogin(): void {
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;

  const init: RequestInit = {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    ...rest,
  };

  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, init);
  } catch (err) {
    // An abort is a deliberate cancellation (TanStack cancelQueries), not a
    // network failure — it must not be classified as retryable.
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new NetworkError(err);
  }

  if (res.status === 401) {
    redirectToLogin();
    throw new ApiError(401, "Unauthorized", null);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload: unknown = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const message =
      isJson && payload && typeof payload === "object" && "detail" in payload
        ? String((payload as { detail: unknown }).detail)
        : res.statusText || `Request failed with status ${res.status}`;
    throw new ApiError(res.status, message, payload, parseRetryAfter(res.headers.get("retry-after")));
  }

  return payload as T;
}

export const apiClient = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "POST", body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PUT", body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: "DELETE" }),
};
